import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { searchAll } from "@/utils/data";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import NewsCard from "@/components/feature/NewsCard";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
  }, [query]);

  usePageMeta({
    title: query ? `“${query}” 的搜索结果 - 黄色在线` : "搜索 - 黄色在线",
    description: query
      ? `在黄色在线搜索“${query}”相关的影视作品与影视资讯。`
      : "在黄色在线搜索电影、电视剧、综艺与影视资讯，快速找到想看的内容。",
    keywords: `黄色在线,影视搜索,${query || "影视资源"},电影搜索,资讯搜索`,
  });

  const result = useMemo(() => searchAll(query), [query]);
  const total = result.movies.length + result.news.length;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    if (q) {
      setSearchParams({ q });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="w-full">
      <section className="w-full bg-primary-900 px-4 py-8 md:px-8 md:py-12">
        <Breadcrumb light items={[{ label: "搜索" }]} />
        <h1 className="mt-4 font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
          搜索影视与资讯
        </h1>
        <form onSubmit={handleSubmit} className="mt-6 max-w-2xl">
          <div className="relative">
            <i
              className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-foreground-400"
              aria-hidden="true"
            />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入片名、演员、导演或资讯关键词"
              className="w-full rounded-md border border-transparent bg-background-50 py-3 pl-12 pr-24 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400"
            />
            {input ? (
              <button
                type="button"
                aria-label="清除关键词"
                onClick={() => {
                  setInput("");
                  setSearchParams({});
                }}
                className="absolute right-[76px] top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-200 hover:text-foreground-700"
              >
                <i className="ri-close-line text-base" aria-hidden="true" />
              </button>
            ) : null}
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </div>
        </form>
      </section>

      <section className="w-full px-4 py-8 md:px-8 md:py-10">
        {query ? (
          <>
            <p className="text-sm text-foreground-500">
              关键词
              <span className="mx-1 rounded-md bg-accent-100 px-2 py-0.5 font-medium text-accent-700">
                {query}
              </span>
              共找到 <span className="font-semibold text-accent-600">{total}</span> 条结果
            </p>

            {total === 0 ? (
              <div className="mt-6 flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-12 text-center">
                <i className="ri-search-eye-line text-4xl text-foreground-300" aria-hidden="true" />
                <h2 className="font-heading text-lg font-bold text-foreground-950">没有找到相关内容</h2>
                <p className="max-w-md text-sm text-foreground-500">
                  换个关键词试试，或者先去逛逛影视分类，看看最新上线的作品。
                </p>
                <div className="mt-1 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/"
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-home-4-line" aria-hidden="true" />
                    返回首页
                  </Link>
                  <Link
                    to="/category/movie"
                    className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
                  >
                    <i className="ri-film-line" aria-hidden="true" />
                    浏览电影
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {result.movies.length > 0 ? (
                  <div className="mt-6">
                    <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                      影视作品
                      <span className="text-sm font-normal text-foreground-400">
                        ({result.movies.length})
                      </span>
                    </h2>
                    <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                      {result.movies.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} />
                      ))}
                    </div>
                  </div>
                ) : null}

                {result.news.length > 0 ? (
                  <div className="mt-10">
                    <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                      <span className="h-5 w-1 rounded-full bg-accent-500" aria-hidden="true" />
                      影视资讯
                      <span className="text-sm font-normal text-foreground-400">
                        ({result.news.length})
                      </span>
                    </h2>
                    <div className="mt-4 flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 p-2 md:p-4">
                      {result.news.map((item) => (
                        <div key={item.id} className="py-2">
                          <NewsCard item={item} />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center gap-5 rounded-lg border border-background-200 bg-background-50 p-12 text-center">
            <i className="ri-search-line text-4xl text-foreground-300" aria-hidden="true" />
            <h2 className="font-heading text-lg font-bold text-foreground-950">开始你的搜索</h2>
            <p className="max-w-md text-sm text-foreground-500">
              输入片名、演员、导演或资讯关键词，例如“星际回响”“悬疑”“周澈”。
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {["星际回响", "悬疑", "周澈", "综艺", "行业动态"].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setSearchParams({ q: tag })}
                  className="cursor-pointer whitespace-nowrap rounded-full bg-accent-100 px-4 py-1.5 text-sm text-accent-700 transition-colors hover:bg-accent-200"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}